import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useSelector, useDispatch, connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { db } from "utils/db";
import { useLiveQuery } from "dexie-react-hooks";
import api from "api";
import "components/shared/occurrence-modal.css";
import AppModal from "components/ui/AppModal/AppModal";
import { clearProjectCache } from "utils/authUtils";

const toCanonicalReportsFolderBase = folderPath => {
  if (!folderPath) return folderPath;
  const clean = String(folderPath).replace(/\/+$/, "");
  const parts = clean.split("/");
  const projetosIdx = parts.indexOf("projetos");
  if (
    projetosIdx !== -1 &&
    parts[projetosIdx + 1] &&
    parts[projetosIdx + 2]
  ) {
    return `projetos/${parts[projetosIdx + 1]}/${parts[projetosIdx + 2]}`;
  }
  if (parts[0] === "projetos" && parts[1] && parts[2]) {
    return `projetos/${parts[1]}/${parts[2]}`;
  }
  return clean;
};

const ReportList = ({ folderPath, token, email }) => {
  const { report: reports } = useSelector(state => state.report || []);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const images = useLiveQuery(() => db.images.toArray());
  const [imageBase64Map, setImageBase64Map] = useState({});
  const [expandedImage, setExpandedImage] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [feedbackModal, setFeedbackModal] = useState({
    open: false,
    variant: "custom",
    title: "",
    message: "",
    confirmText: "OK",
    cancelText: null
  });

  const closeFeedbackModal = () =>
    setFeedbackModal(prev => ({ ...prev, open: false }));

  const handleFeedbackConfirm = () => {
    closeFeedbackModal();
    clearProjectCache(email);

    // Navega diretamente para a lista de relatórios do projeto
    const parts = (folderPath || "").split("/").filter(Boolean);
    const projectName = parts[1];  // projetos/{name}/{date}/{product}
    const projectDate = parts[2];
    const productType = parts[3];

    if (projectName && projectDate) {
      const reportsState = {
        folder_path: `projetos/${projectName}/${projectDate}/relatorios/`,
        project: { name: projectName, type: "relatorios" },
        date: projectDate
      };

      if (productType === "gemeo_digital") {
        // Gêmeo Digital carrega via window.location.href (reload completo), então o Potree
        // deixa resíduos no DOM (overflow:hidden, WebGL, etc.). Usar reload completo aqui
        // garante que a página de relatórios carregue limpa.
        sessionStorage.setItem("listReportsState", JSON.stringify(reportsState));
        window.location.href = "/reports/list";
      } else {
        navigate("/reports/list", { state: reportsState });
      }
    } else {
      navigate("/projects");
    }
  };

  const getImageBase64 = async key => {
    if (images && images.length > 0 && key) {
      const [image] = images.filter(i => i.key === key);
      if (image) {
        const response = await fetch(image.img);
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      }
    }
    return null;
  };

  const fetchImages = async () => {
    const base64Map = {};
    for (const item of reports) {
      base64Map[item.key] = await getImageBase64(item.key);
    }
    setImageBase64Map(base64Map);
  };

  const handleOpen = () => {
    fetchImages();
    setIsOpenModal(true);
    setErrorMsg("");
  };

  const handleClose = () => {
    setIsOpenModal(false);
  };

  useEffect(() => {
    if (isOpenModal && reports.length > 0) {
      fetchImages();
    }
    if (isOpenModal && reports.length === 0) {
      setIsOpenModal(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenModal, reports]);

  const handleFinish = async () => {
    setIsLoading(true);
    setErrorMsg("");
    try {
      const results = await Promise.all(
        reports.map(async report => {
          const imageBase64 = imageBase64Map[report.key];
          const payload = {
            folder_path: `${toCanonicalReportsFolderBase(folderPath)}/`,
            image_data: imageBase64,
            image_info: {
              titulo: report.titulo,
              descricao: report.descricao,
              acao: report.acao,
              severidade: report.severidade
            }
          };

          const response = await api.post(`/projects/save_snapshot`, payload, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
            }
          });
          if (response.data?.status === "error") {
            throw new Error(response.data.message || "Erro ao salvar snapshot");
          }
          return response.status === 200 && response.data?.status !== "error";
        })
      );

      if (results.every(status => status)) {
        setFeedbackModal({
          open: true,
          variant: "success",
          title: "Relatórios salvos!",
          message: "Confira a sessão de relatórios.",
          confirmText: "Ir para Relatórios",
          cancelText: "Fechar"
        });
        dispatch({ type: "CLEAR_REPORT" });
        handleClose();
      } else {
        setErrorMsg("Houve erros ao salvar alguns relatórios.");
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail?.[0]?.msg || "Erro ao salvar relatórios";
      setErrorMsg(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const removeReport = id => {
    dispatch({ type: "REMOVE_REPORT", payload: id });
  };

  const handleBackdropClick = e => {
    if (e.target === e.currentTarget) handleClose();
  };

  return (
    <>
      <div className="occ-trigger-wrap">
        {reports.length > 0 && (
          <button
            type="button"
            onClick={handleOpen}
            className="occ-trigger"
            aria-label="Ver lista de ocorrências"
            title="Lista de Ocorrências"
          >
            <i className="fas fa-file-alt" />
          </button>
        )}
      </div>

      {isOpenModal && createPortal(
        <div className="occ-list-overlay" onClick={handleBackdropClick}>
          <div className="occ-list">
            <div className="occ-list__header">
              <div className="occ-list__header-inner">
                <span className="occ-list__accent">Ocorrências</span>
                <h2 className="occ-list__title">Lista de Ocorrências</h2>
                <p className="occ-list__subtitle">
                  Revise as ocorrências capturadas e salve os relatórios.
                </p>
              </div>
              <button
                type="button"
                className="occ-list__close"
                onClick={handleClose}
                aria-label="Fechar"
              >
                <i className="fas fa-times" />
              </button>
            </div>

            <div className="occ-list__body">
              {reports.length === 0 ? (
                <div className="occ-list__empty">
                  <i className="fas fa-file-excel" />
                  <p>Nenhuma ocorrência adicionada.</p>
                  <p className="occ-list__empty-hint">
                    Clique no ícone de câmera para capturar e adicionar ocorrências.
                  </p>
                </div>
              ) : (
                <div className="occ-list__items">
                  {reports.map(item => (
                    <div key={item.key} className="occ-list-item">
                      <div
                        className="occ-list-item__img-wrap"
                        onClick={() => setExpandedImage(imageBase64Map[item.key])}
                      >
                        <img
                          src={imageBase64Map[item.key] || ""}
                          alt={item.titulo}
                          className="occ-list-item__img"
                        />
                        <div className="occ-list-item__img-overlay">
                          <i className="fas fa-search-plus" />
                        </div>
                      </div>

                      <div className="occ-list-item__content">
                        <div className="occ-list-item__header">
                          <h6 className="occ-list-item__title">{item.titulo}</h6>
                          <span
                            className="occ-list-item__severity"
                            data-sev={item.severidade}
                          >
                            {item.severidade}
                          </span>
                        </div>
                        <div className="occ-list-item__details">
                          <p className="occ-list-item__desc">
                            <strong>Descrição:</strong> {item.descricao}
                          </p>
                          <p className="occ-list-item__action">
                            <strong>Ação:</strong> {item.acao}
                          </p>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => removeReport(item.key)}
                        className="occ-list-item__remove"
                        aria-label="Remover"
                      >
                        <i className="fas fa-trash" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {errorMsg && <div className="occ-list__error">{errorMsg}</div>}

            {reports.length > 0 && (
              <div className="occ-list__footer">
                <span className="occ-list__count">
                  {reports.length} ocorrência{reports.length !== 1 ? "s" : ""}
                </span>
                <button
                  type="button"
                  onClick={handleFinish}
                  disabled={isLoading}
                  className="occ-list__save"
                >
                  {isLoading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      />
                      Salvando...
                    </>
                  ) : (
                    <>Salvar Relatórios &rarr;</>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {expandedImage && createPortal(
        <div
          className="occ-preview-overlay"
          onClick={() => setExpandedImage(null)}
        >
          <img
            src={expandedImage}
            alt="Visualização expandida"
            className="occ-preview-img"
          />
        </div>,
        document.body
      )}

      <AppModal
        isOpen={feedbackModal.open}
        onClose={closeFeedbackModal}
        allowOutsideClick={false}
        variant={feedbackModal.variant}
        title={feedbackModal.title}
        message={feedbackModal.message}
        confirmText={feedbackModal.confirmText}
        cancelText={feedbackModal.cancelText}
        onConfirm={handleFeedbackConfirm}
        onCancel={closeFeedbackModal}
      />
    </>
  );
};

const mapStateToProps = state => ({
  token: state.auth.accessToken,
  email: state.auth.email
});

export default connect(mapStateToProps)(ReportList);
